import React, { createContext, useContext, useState, useEffect } from 'react';

interface InternalNote {
  id: string;
  content: string;
  createdAt: string;
  createdBy: string;
}

interface SupportTicket {
  id: string;
  customer: {
    name: string;
    email: string;
    phone: string;
    description: string;
  };
  status: 'Open' | 'In Progress' | 'Resolved';
  priority: 'High' | 'Medium' | 'Low';
  createdAt: string;
  lastUpdated: string;
  isRead: boolean;
  messages: any[];
  expiresAt?: string;
  internalNotes: InternalNote[];
}

interface NotificationContextType {
  supportTickets: SupportTicket[];
  unreadCount: number;
  markTicketAsRead: (ticketId: string) => void;
  markAllTicketsAsRead: () => void;
  updateTicket: (updatedTicket: SupportTicket) => void;
  addInternalNote: (ticketId: string, note: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [supportTickets, setSupportTickets] = useState<SupportTicket[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    // Check for tickets and clean up expired ones every second
    const interval = setInterval(() => {
      try {
        const now = new Date();
        const tickets: SupportTicket[] = JSON.parse(localStorage.getItem('supportTickets') || '[]');
        
        // Ensure all tickets have internalNotes array
        const ticketsWithNotes = tickets.map(ticket => ({
          ...ticket,
          internalNotes: ticket.internalNotes || []
        }));
        
        // Filter out expired tickets (older than 30 days)
        const validTickets = ticketsWithNotes.filter(ticket => {
          const expiryDate = ticket.expiresAt ? new Date(ticket.expiresAt) : new Date(ticket.createdAt);
          return expiryDate > now;
        });

        // Update unread count
        const unreadTickets = validTickets.filter(ticket => !ticket.isRead).length;
        
        setSupportTickets(validTickets);
        setUnreadCount(unreadTickets);

        // Save filtered tickets back to storage
        if (validTickets.length !== tickets.length) {
          localStorage.setItem('supportTickets', JSON.stringify(validTickets));
        }
      } catch (error) {
        console.error('Error checking support tickets:', error);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const markTicketAsRead = (ticketId: string) => {
    try {
      const updatedTickets = supportTickets.map(ticket => 
        ticket.id === ticketId ? { ...ticket, isRead: true } : ticket
      );
      localStorage.setItem('supportTickets', JSON.stringify(updatedTickets));
      setSupportTickets(updatedTickets);
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking ticket as read:', error);
    }
  };

  const markAllTicketsAsRead = () => {
    try {
      const updatedTickets = supportTickets.map(ticket => ({ ...ticket, isRead: true }));
      localStorage.setItem('supportTickets', JSON.stringify(updatedTickets));
      setSupportTickets(updatedTickets);
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all tickets as read:', error);
    }
  };

  const updateTicket = (updatedTicket: SupportTicket) => {
    try {
      const updatedTickets = supportTickets.map(ticket =>
        ticket.id === updatedTicket.id ? updatedTicket : ticket
      );
      localStorage.setItem('supportTickets', JSON.stringify(updatedTickets));
      setSupportTickets(updatedTickets);
    } catch (error) {
      console.error('Error updating ticket:', error);
    }
  };

  const addInternalNote = (ticketId: string, noteContent: string) => {
    try {
      const updatedTickets = supportTickets.map(ticket => {
        if (ticket.id === ticketId) {
          const newNote: InternalNote = {
            id: Date.now().toString(),
            content: noteContent,
            createdAt: new Date().toISOString(),
            createdBy: 'Admin User' // In a real app, this would come from the authenticated user
          };
          
          return {
            ...ticket,
            internalNotes: [...(ticket.internalNotes || []), newNote],
            lastUpdated: new Date().toISOString()
          };
        }
        return ticket;
      });
      
      localStorage.setItem('supportTickets', JSON.stringify(updatedTickets));
      setSupportTickets(updatedTickets);
    } catch (error) {
      console.error('Error adding internal note:', error);
    }
  };

  return (
    <NotificationContext.Provider value={{ 
      supportTickets, 
      unreadCount, 
      markTicketAsRead, 
      markAllTicketsAsRead,
      updateTicket,
      addInternalNote
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}